import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, Outlet } from "react-router-dom"
import {
  AppBar,
  Toolbar,
  Button,
  List,
  ListItem,
  IconButton,
  Container,
  Box,
  Typography,
  styled,
} from "@mui/material"
import { makeStyles } from "@mui/styles"
import MenuIcon from "@mui/icons-material/Menu"
import CloseIcon from "@mui/icons-material/Close"

import IconPower from "assets/icons/power.svg"

import { useAppDispatch } from "redux/hooks"
import { SubscriptionContextProvider } from "redux/contexts/subscription"
import { logout } from "redux/features/auth/authSlice"
import { AsideComponent as Aside } from "components/ui/Aside"
import Image from "components/ui/Image"
import ClientMenu from "components/Layout/ClientMenu"
import ClientManager from "components/Layout/ClientManager"
import LanguageSwitcher from "components/Layout/LanguageSwitcher"
import MobileTopList from "components/Layout/MobileTopList"
import Logo from "components/Layout/Logo"
import Notice from "components/Layout/Notice"
import Dialog from "components/Layout/Dialog"
import Footer from "components/Layout/Footer"
import Tracker from "components/Layout/Tracker"
// import Tawk from "components/Layout/Tawk"
import Settings from "components/Layout/Settings"
import { sizes } from "theme"

const ProtectedLayout = () => {
  const classes = useStyles()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const [mobileOpen, setMobileOpen] = useState(false)

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleLogout = () => {
    if (mobileOpen) {
      handleDrawerToggle()
    }
    dispatch(logout())
  }

  return (
    <SubscriptionContextProvider>
      <Settings />
      <Dialog />
      <Notice />

      <Header elevation={0} color="transparent" position="static">
        <HeaderToolbar>
          <HeaderLogo to="/">
            <Logo white={false} />
          </HeaderLogo>
          <Box>
            <LanguageSwitcher signedIn />
            <NavButton color="primary" edge="end" onClick={handleDrawerToggle}>
              <MenuIcon />
            </NavButton>
          </Box>
        </HeaderToolbar>
      </Header>

      <Nav component="nav">
        <Aside
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          classes={{
            paper: classes.drawerPaperMobile,
          }}
          ModalProps={{
            keepMounted: true,
          }}
        >
          <NavBar>
            <Logo white />
            <NavButton color="primary" edge="end" onClick={handleDrawerToggle}>
              <CloseIcon />
            </NavButton>
          </NavBar>
          <MobileTopList signedIn onClick={handleDrawerToggle} />
          <BottomList>
            <ListItem>
              <ListItemButton onClick={handleLogout}>
                {t("menu.logout")}
              </ListItemButton>
            </ListItem>
          </BottomList>
        </Aside>

        <Aside
          variant="permanent"
          classes={{
            paper: classes.drawerPaperDesktop,
          }}
          open
        >
          <SidebarLogo>
            <Link to="/">
              <Logo white />
            </Link>
          </SidebarLogo>

          <SidebarMenu>
            <ClientMenu />
          </SidebarMenu>

          <SidebarManager>
            <ClientManager />
          </SidebarManager>

          <SidebarAccount>
            <List sx={{ width: "100%" }}>
              <ListItem>
                <Logout onClick={handleLogout}>
                  <LogoutTitle>{t("menu.logout")}</LogoutTitle>
                  <Image src={IconPower} alt={t("menu.logout")} />
                </Logout>
              </ListItem>
            </List>
          </SidebarAccount>
        </Aside>
      </Nav>

      <Main component="main">
        <Container>
          <Outlet />
          <Footer isProtected />
        </Container>
      </Main>

      <Tracker />
      {/* <Tawk /> */}
    </SubscriptionContextProvider>
  )
}

export default ProtectedLayout

const Header = styled(AppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer - 1,
  [theme.breakpoints.up("lg")]: {
    maxWidth: `${sizes.contentWidth}px`,
    marginLeft: `${sizes.offset}px`,
    padding: "34px 0",
  },
  [theme.breakpoints.down("lg")]: {
    padding: "6px",
  },
}))

const HeaderToolbar = styled(Toolbar)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  [theme.breakpoints.up("lg")]: {
    justifyContent: "flex-end",
    padding: "0 16px 0 0",
  },
}))

const HeaderLogo = styled(Link)(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    display: "none",
  },
}))

const Main = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  maxWidth: `${sizes.contentWidth}px`,
  [theme.breakpoints.up("lg")]: {
    marginLeft: `${sizes.offset}px`,
  },
  [theme.breakpoints.down("lg")]: {
    margin: "0 auto",
  },
}))

const Nav = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    display: "none",
  },
}))

const NavButton = styled(IconButton)(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    display: "none !important",
  },
}))

const NavBar = styled(Toolbar)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}))

const BottomList = styled(List)`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: flex-end;
`

const ListItemButton = styled(Button)`
  background: ${({ theme }) => theme.palette.primary.main};
  color: ${({ theme }) => theme.palette.common.white};
  border-radius: 28px;
  display: flex;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 17px;
  padding: 16px 14px;
  text-transform: initial;
  width: 100%;
`

const SidebarLogo = styled(Toolbar)`
  align-items: center;
  display: flex;
  justify-content: flex-start;
  padding: 43px 24px;
`

const SidebarMenu = styled(Box)`
  display: flex;
  flex-direction: column;
`

const SidebarManager = styled(Box)`
  display: flex;
  flex-grow: 1;
  align-items: flex-end;
  margin-bottom: 20px;
`

const SidebarAccount = styled(Box)`
  display: flex;
  align-items: flex-end;
  margin-bottom: 20px;
`

const Logout = styled(Button)`
  color: ${({ theme }) => theme.palette.common.white};
  display: flex;
  padding: 0;
  text-transform: initial;
  width: 100%;
  &:hover {
    background-color: transparent;
  }
`

const LogoutTitle = styled(Typography)`
  font-size: 0.875rem;
  line-height: 17px;
  font-weight: 500;
  margin-right: 20px;
`

const useStyles = makeStyles(() => ({
  drawerPaperDesktop: {
    width: sizes.drawerWidth,
  },
  drawerPaperMobile: {
    width: "100%",
  },
}))
